import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    Box,
    Button,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    IconButton,
    Chip,
    Stack,
    Alert,
    CircularProgress
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    CloudUpload as CloudUploadIcon,
    CalendarToday as CalendarIcon,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import { useLocation, useNavigate } from 'react-router-dom';
import { initializeDB } from '../../../../config/db';
import {
    WELDING_FORM_STORE,
    EMPLOYEE_STORE,
    JOB_STORE,
    INI_STORE,
    STATUS_PROCESS,
    STATUS_SEND,
    STATUS_FINISH
} from '../../../../config/constants';
import { saveWeldingForm, markWeldingFormSend, markWeldingFormStatus } from '../../../../services/weldingFormService';
import SignatureModalWithAuto from '../../../common/SignatureModalWithAuto';
import axiosInstance from "../../../../axiosInstance";
import { hasSignatureContent } from '../../../../utils/utils';
import { localDateString } from '../../../../utils/localDate';

// Pulsing upload button style
const pulsingUploadButton = {
    position: 'relative',
    overflow: 'visible',
    animation: 'pulse 2s ease-in-out infinite',
    '@keyframes pulse': {
        '0%': {
            transform: 'scale(1)',
            boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.7)',
        },
        '50%': {
            transform: 'scale(1.05)',
            boxShadow: '0 0 0 10px rgba(244, 67, 54, 0)',
        },
        '100%': {
            transform: 'scale(1)',
            boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)',
        },
    },
};

/**
 * Format date helper
 */
const formatDateTime = (dateValue) => {
    if (!dateValue) return 'Not sent yet';
    
    if (typeof dateValue === 'object' && dateValue?.date) {
        return new Date(dateValue.date).toLocaleString('en-US');
    }
    
    if (dateValue) {
        return new Date(dateValue).toLocaleString('en-US');
    }
    
    return 'Not sent yet';
};

export default function WeldingQvalForm() {
    const { state } = useLocation() || {};
    const selectedTrainingDue = state?.selectedTrainingDue || null;
    const trainingDueIdFromRoute = selectedTrainingDue?.id || state?.trainingDueId || null;
    const navigate = useNavigate();
    const sigPadRefEmployee = useRef(null);

    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [employees, setEmployees] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [showSignatureEmployee, setShowSignatureEmployee] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    // Generate signature function
    const generateSignature = (text) => {
        const tempCanvas = document.createElement("canvas");
        const tempContext = tempCanvas.getContext("2d");
        const fontSize = 48;
        const fontFamily = "Pacifico";
        tempContext.font = `${fontSize}px ${fontFamily}`;

        const textMetrics = tempContext.measureText(text);
        const textWidth = textMetrics.width;
        const padding = 20;

        const canvasWidth = Math.ceil(textWidth + padding);
        const canvasHeight = fontSize + 20;

        const canvas = document.createElement("canvas");
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        const context = canvas.getContext("2d");
        context.font = `${fontSize}px ${fontFamily}`;
        context.textBaseline = "middle";
        context.fillStyle = "black";

        context.fillText(text, 10, canvasHeight / 2);
        return canvas.toDataURL("image/png");
    };

    const loadData = useCallback(async () => {
        const db = await initializeDB();

        // Load employees
        const txEmp = db.transaction(EMPLOYEE_STORE, 'readonly');
        const storeEmp = txEmp.objectStore(EMPLOYEE_STORE);
        const allEmployees = await storeEmp.getAll();
        await txEmp.done;
        const employeeId = localStorage.getItem('employeeId');
        let defaultWitness = null;
        if (employeeId) {
            defaultWitness = allEmployees.find(e => e.employeeId === employeeId);
        }

        // Load jobs
        const txJobs = db.transaction(JOB_STORE, 'readonly');
        const storeJobs = txJobs.objectStore(JOB_STORE);
        const jobList = await storeJobs.getAll();
        await txJobs.done;

        // Load existing forms
        const txWelding = db.transaction(WELDING_FORM_STORE, 'readonly');
        const storeWelding = txWelding.objectStore(WELDING_FORM_STORE);
        const allForms = await storeWelding.getAll();
        await txWelding.done;

        setEmployees(allEmployees);
        setJobs(jobList);

        // Filter active forms
        const activeForms = allForms.filter(f => f.status !== STATUS_SEND);
        let foundForm = null;

        if (trainingDueIdFromRoute) {
            foundForm = activeForms.find(f => f.trainingDueId === trainingDueIdFromRoute);
            if (!foundForm) {
                foundForm = {
                    id: Date.now(),
                    trainingDueId: trainingDueIdFromRoute,
                    employeeName: selectedTrainingDue?.fullName || '',
                    welderId: '',
                    process: selectedTrainingDue?.course || '',
                    qvalDate: localDateString(),
                    requalDate: '',
                    witnessId: defaultWitness?.id || '',
                    witnessName: defaultWitness?.FullName || '',
                    jobId: '',
                    status: STATUS_PROCESS,
                    employeeSignature: null,
                    employeeSignedAt: null,
                    managerName: '',
                    managerSignature: null,
                    managerSignedAt: null,
                    lastSentAt: null,
                };
                await saveWeldingForm(foundForm);
            }
        } else {
            foundForm = activeForms[0] || {
                id: Date.now(),
                employeeName: selectedTrainingDue?.fullName || '',
                welderId: '',
                process: selectedTrainingDue?.course || '',
                qvalDate: localDateString(),
                requalDate: '',
                witnessId: defaultWitness?.id || '',
                witnessName: defaultWitness?.FullName || '',
                jobId: '',
                status: STATUS_PROCESS,
                employeeSignature: null,
                employeeSignedAt: null,
                managerName: '',
                managerSignature: null,
                managerSignedAt: null,
                lastSentAt: null,
            };
            await saveWeldingForm(foundForm);
        }

        // Load welder ID
        if (foundForm.employeeName) {
            const emp = allEmployees.find(e => e.FullName === foundForm.employeeName);
            if (emp && emp.welderId) {
                foundForm.welderId = emp.welderId;
            }
        }

        setFormData(foundForm);
        setLoading(false);
    }, [trainingDueIdFromRoute, selectedTrainingDue]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleUpdateFormData = useCallback((field, value) => {
        const updatedData = { ...formData, [field]: value };
        setFormData(updatedData);
        saveWeldingForm(updatedData);
    }, [formData]);

    const handleSelectChange = useCallback((field, e) => {
        const value = e.target.value;
        let updatedData = { ...formData, [field]: value };
        
        if (field === 'witnessId') {
            const witness = employees.find(emp => emp.id === value);
            updatedData.witnessName = witness?.FullName || '';
        }
        
        setFormData(updatedData);
        saveWeldingForm(updatedData);
    }, [formData, employees]);

    const handleDeleteSignature = useCallback((type) => {
        let updatedData = { ...formData };
        if (type === 'employee') {
            updatedData.employeeSignature = null;
            updatedData.employeeSignedAt = null;
        } else if (type === 'manager') {
            updatedData.managerSignature = null;
            updatedData.managerSignedAt = null;
            updatedData.managerName = '';
        }
        setFormData(updatedData);
        saveWeldingForm(updatedData);
    }, [formData]);

    const handleSignEmployee = useCallback(() => {
        if (hasSignatureContent(sigPadRefEmployee)) {
            const signatureData = sigPadRefEmployee.current.toDataURL();
            const updatedData = {
                ...formData,
                employeeSignature: signatureData,
                employeeSignedAt: new Date().toISOString(),
            };
            setFormData(updatedData);
            saveWeldingForm(updatedData);
            setShowSignatureEmployee(false);
        }
    }, [formData]);

    const handleSignManager = useCallback(() => {
        const managerName = localStorage.getItem('employeeName') || 'Manager';
        const signatureData = generateSignature(managerName);
        const updatedData = {
            ...formData,
            managerName: managerName,
            managerSignature: signatureData,
            managerSignedAt: new Date().toISOString(),
        };
        setFormData(updatedData);
        saveWeldingForm(updatedData);
    }, [formData]);

    const sendData = useCallback(async () => {
        const dataToSend = { ...formData };
        
        if (!(dataToSend.employeeName &&
            dataToSend.process &&
            dataToSend.qvalDate &&
            dataToSend.witnessId &&
            dataToSend.jobId &&
            dataToSend.employeeSignature
        )) {
            alert('Please fill out all fields and provide both signatures before sending.');
            return;
        }

        setIsUploading(true);
        try {
            const payload = {
                ...dataToSend,
                formType: 'welding',
                course: formData.process,
                sendBy: localStorage.getItem('userId'),
            };

            await axiosInstance.post('/training/form/save', payload);

            if (dataToSend.id) {
                await markWeldingFormStatus(dataToSend.id, STATUS_SEND);
            }
            
            const updatedData = {
                ...formData,
                status: STATUS_SEND,
                lastSentAt: new Date().toISOString(),
            };
            setFormData(updatedData);
            
            alert('Data sent successfully.');
            navigate('/training-due');
        } catch (err) {
            if (dataToSend.id) {
                await markWeldingFormStatus(dataToSend.id, STATUS_FINISH);
            }
            
            const updatedData = {
                ...formData,
                status: STATUS_FINISH,
            };
            setFormData(updatedData);
            
            alert('Data was not saved but saved locally');
        } finally {
            setIsUploading(false);
        }
    }, [formData, navigate]);

    if (loading) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '100vh' 
            }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!formData) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error">Failed to load form data</Alert>
            </Box>
        );
    }

    const canSign = formData.employeeName && formData.welderId;
    const canSend = formData.employeeSignature && formData.witnessId && formData.jobId;
    const isReadyToSend = formData.status === STATUS_PROCESS && canSend;

    // Get status color
    const getStatusColor = () => {
        switch(formData.status) {
            case STATUS_PROCESS: return 'warning';
            case STATUS_SEND: return 'success';
            case STATUS_FINISH: return 'info';
            default: return 'default';
        }
    };

    return (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            height: '100vh',
            bgcolor: 'background.default'
        }}>
            {/* Fixed Header */}
            <Paper 
                elevation={2}
                sx={{ 
                    position: 'sticky',
                    top: 0,
                    zIndex: 1100,
                    borderRadius: 0,
                    borderBottom: 1,
                    borderColor: 'divider'
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                            <IconButton 
                                onClick={() => navigate('/training-due')}
                                size="large"
                                sx={{ 
                                    bgcolor: 'primary.light',
                                    color: 'primary.contrastText',
                                    '&:hover': {
                                        bgcolor: 'primary.main',
                                    }
                                }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            
                            <Box>
                                <Typography variant="h5" fontWeight="bold">
                                    Welding Qualification Form
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                    <Chip
                                        label={formData.status?.toUpperCase() || 'NEW'}
                                        color={getStatusColor()}
                                        size="small"
                                        sx={{ fontWeight: 'bold' }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        <CalendarIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                        {formData.qvalDate}
                                    </Typography>
                                    {formData.employeeName && (
                                        <Typography variant="caption" color="text.secondary">
                                            • {formData.employeeName}
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>
                        </Stack>

                        {/* Send Button in Header */}
                        <Box sx={{ position: 'relative' }}>
                            <Button
                                variant="contained"
                                color={isReadyToSend ? "error" : "inherit"}
                                onClick={sendData}
                                disabled={!canSend || isUploading}
                                startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                                sx={isReadyToSend && !isUploading ? pulsingUploadButton : {}}
                            >
                                {isUploading ? 'Sending...' : 'Send'}
                            </Button>
                            {formData.lastSentAt && (
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        position: 'absolute',
                                        bottom: -20,
                                        right: 0,
                                        whiteSpace: 'nowrap',
                                        color: 'text.secondary'
                                    }}
                                >
                                    Last sent: {formatDateTime(formData.lastSentAt)}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                </Box>
            </Paper>

            {/* Scrollable Content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                {/* Basic Information */}
                <Paper sx={{ mb: 3, p: 3 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                        Basic Information
                    </Typography>
                    <TableContainer>
                        <Table>
                            <TableBody>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600, width: 200 }}>Employee Name</TableCell>
                                    <TableCell>
                                        <Typography>{formData.employeeName || ''}</Typography>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Welder ID</TableCell>
                                    <TableCell>
                                        <Typography>{formData.welderId || '—'}</Typography>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Process</TableCell>
                                    <TableCell>
                                        <Typography>{formData.process || ''}</Typography>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Qualification Date</TableCell>
                                    <TableCell>
                                        <TextField
                                            type="date"
                                            value={formData.qvalDate || ''}
                                            onChange={e => handleUpdateFormData('qvalDate', e.target.value)}
                                            size="small"
                                            sx={{ width: '170px' }}
                                        />
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Re-qualification Date</TableCell>
                                    <TableCell>
                                        <Typography>{formData.requalDate || '—'}</Typography>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Witness</TableCell>
                                    <TableCell>
                                        <Autocomplete
                                            value={employees.find(emp => emp.id === formData.witnessId) || null}
                                            onChange={(event, newValue) => {
                                                const syntheticEvent = { target: { value: newValue ? newValue.id : '' } };
                                                handleSelectChange('witnessId', syntheticEvent);
                                            }}
                                            options={employees}
                                            getOptionLabel={option => option.FullName || ''}
                                            renderInput={params => (
                                                <TextField {...params} label="Select witness" variant="outlined" size="small" />
                                            )}
                                            sx={{ minWidth: '200px' }}
                                        />
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Job # / Name</TableCell>
                                    <TableCell>
                                        <Autocomplete
                                            value={jobs.find(job => job.id === formData.jobId) || null}
                                            onChange={(event, newValue) => {
                                                const syntheticEvent = { target: { value: newValue ? newValue.id : '' } };
                                                handleSelectChange('jobId', syntheticEvent);
                                            }}
                                            options={jobs}
                                            getOptionLabel={option => `${option.Job} - ${option.JobName}` || ''}
                                            renderInput={params => (
                                                <TextField {...params} label="Select Job" variant="outlined" size="small" />
                                            )}
                                            sx={{ minWidth: '250px' }}
                                        />
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Employee Signature */}
                <Paper sx={{ mb: 3, p: 3 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                        Employee Signature
                    </Typography>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Full Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Signature</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Date Signed</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell>{formData.employeeName || '—'}</TableCell>
                                    <TableCell>
                                        {formData.employeeSignature ? (
                                            <img
                                                src={formData.employeeSignature}
                                                alt="Employee signature"
                                                style={{ maxWidth: '200px', height: 'auto' }}
                                            />
                                        ) : (
                                            <Typography variant="body2" color="textSecondary">No Signature</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {formData.employeeSignedAt
                                            ? new Date(formData.employeeSignedAt).toLocaleString()
                                            : <Typography variant="body2" color="textSecondary">Not Signed</Typography>}
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            disabled={!canSign}
                                            onClick={() => setShowSignatureEmployee(true)}
                                            sx={{ mr: 1 }}
                                        >
                                            {formData.employeeSignature ? 'Update' : 'Sign'}
                                        </Button>
                                        {formData.employeeSignature && (
                                            <Button 
                                                variant="outlined" 
                                                color="error" 
                                                onClick={() => handleDeleteSignature('employee')}
                                            >
                                                Delete
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Manager Signature */}
                <Paper sx={{ mb: 3, p: 3 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                        Manager Signature
                    </Typography>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Manager Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Signature</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Date Signed</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell>{formData.managerName || '—'}</TableCell>
                                    <TableCell>
                                        {formData.managerSignature ? (
                                            <img
                                                src={formData.managerSignature}
                                                alt="Manager signature"
                                                style={{ maxWidth: '200px', height: 'auto' }}
                                            />
                                        ) : (
                                            <Typography variant="body2" color="textSecondary">No Signature</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {formData.managerSignedAt
                                            ? new Date(formData.managerSignedAt).toLocaleString()
                                            : <Typography variant="body2" color="textSecondary">Not Signed</Typography>}
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleSignManager}
                                        >
                                            {formData.managerSignature ? 'Update' : 'Auto Sign'}
                                        </Button>
                                        {formData.managerSignature && (
                                            <Button 
                                                variant="outlined" 
                                                color="error" 
                                                onClick={() => handleDeleteSignature('manager')}
                                                sx={{ ml: 1 }}
                                            >
                                                Delete
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>

            {/* Signature Modal */}
            <SignatureModalWithAuto
                open={showSignatureEmployee}
                onClose={() => setShowSignatureEmployee(false)}
                sigPadRef={sigPadRefEmployee}
                employeeName={formData.employeeName || 'Employee'}
                onAccept={handleSignEmployee}
            />
        </Box>
    );
}